// Pricing plans data structure for easy updates without touching UI code

export interface PricingPlan {
  id: string;
  name: string;
  tier: 'base' | 'premium' | 'premium_plus' | 'unlimited_owl';
  isPopular?: boolean;
  pricing: {
    monthly: number;
    annually: number;
    annualSavings: number;
  };
  limits: {
    invoicesPerMonth: number | 'unlimited';
    quotesPerMonth: number | 'unlimited';
  };
  features: string[];
  cumulativeFeatures?: string[]; // Features inherited from lower tiers
}

export interface IncludedFeatureCategory {
  category: string;
  icon: string; // lucide icon name
  items: string[];
}

export interface CustomPlanFeature {
  feature: string;
}

// Additional seat pricing
export const additionalSeatPrice = {
  monthly: 9,
};

// Main pricing plans
export const pricingPlans: PricingPlan[] = [
  {
    id: 'base',
    name: 'Base',
    tier: 'base',
    pricing: {
      monthly: 7,
      annually: 4,
      annualSavings: 36,
    },
    limits: {
      invoicesPerMonth: 10,
      quotesPerMonth: 10,
    },
    features: [
      '10 invoices per month',
      '10 quotes per month',
      'Unlimited purchase orders & credit memos',
      'Accept credit card and ACH payments',
      'Read receipts',
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    tier: 'premium',
    isPopular: true,
    pricing: {
      monthly: 39,
      annually: 29,
      annualSavings: 120,
    },
    limits: {
      invoicesPerMonth: 'unlimited',
      quotesPerMonth: 'unlimited',
    },
    features: [
      'Unlimited invoices & quotes',
      'Due date reminders and follow-ups',
      'Premium invoice & quote designs',
      'Custom contracts & client signatures',
    ],
    cumulativeFeatures: [
      'Everything in Base',
    ],
  },
  {
    id: 'premium_plus',
    name: 'Premium Plus',
    tier: 'premium_plus',
    pricing: {
      monthly: 139,
      annually: 99,
      annualSavings: 480,
    },
    limits: {
      invoicesPerMonth: 'unlimited',
      quotesPerMonth: 'unlimited',
    },
    features: [
      'Recurring invoices & billing',
      'Sync to QuickBooks',
      'Phone support',
    ],
    cumulativeFeatures: [
      'Everything in Premium',
    ],
  },
  {
    id: 'unlimited_owl',
    name: 'Unlimited Owl',
    tier: 'unlimited_owl',
    pricing: {
      monthly: 699,
      annually: 499,
      annualSavings: 2400,
    },
    limits: {
      invoicesPerMonth: 'unlimited',
      quotesPerMonth: 'unlimited',
    },
    features: [
      'Priority phone support',
      'Dedicated onboarding specialist',
      '25 team seats included',
    ],
    cumulativeFeatures: [
      'Everything in Premium Plus',
    ],
  },
];

// Custom plan features (contact us)
export const customPlanFeatures: CustomPlanFeature[] = [
  { feature: 'Custom data import' },
  { feature: 'Custom integrations' },
];

// Features included in every plan
export const includedInEveryPlan: IncludedFeatureCategory[] = [
  {
    category: 'Documents',
    icon: 'FileText',
    items: [
      'Quoting',
      'Invoicing',
      'Credit memos',
      'Purchase orders',
      'Drafts of all document types',
    ],
  },
  {
    category: 'Client Management',
    icon: 'Users',
    items: [
      'Manage clients',
      'Track payments',
      'Client reviews',
      'Import clients',
    ],
  },
  {
    category: 'Line Items',
    icon: 'Package',
    items: [
      'Store line items',
      'Create item bundles',
      'Import line items',
    ],
  },
  {
    category: 'Payments',
    icon: 'CreditCard',
    items: [
      'Request quote deposits',
      'Multiple currency options',
      'Manage custom taxes and discounts',
    ],
  },
  {
    category: 'Communication',
    icon: 'Bell',
    items: [
      'Email reminders',
      'Real-time notifications',
    ],
  },
  {
    category: 'Reporting',
    icon: 'BarChart3',
    items: [
      'Client reports',
      'Invoice reports',
      'Payment reports',
      'Profit reports',
    ],
  },
  {
    category: 'Mobile',
    icon: 'Smartphone',
    items: [
      'Free iOS app',
      'Free Android app',
    ],
  },
];

// Helper function to get plan by ID
export function getPlanById(id: string): PricingPlan | undefined {
  return pricingPlans.find((plan) => plan.id === id);
}

// Helper function to format price
export function formatPrice(price: number): string {
  return `$${price}`;
}
